import { createSelector } from 'reselect';

import { IInternetPackage } from '@r1-frontend/api-domru/api-content/v1/internet-tariffs/dto/ResponseDto';

import { ISelectedFilters } from '~/src/entities/filteredBundles';
import { ITariff } from '~/src/entities/formattedTariff';
import { formattingBundleForCard, formattingTariffForCard } from '~/src/helpers/formatter';
import { TState } from '~/src/store';
import { getTariffsSelector } from '~/src/store/selectors/bundles';

export const selectAllTariffs = createSelector(
    [
        getTariffsSelector,
        (state: TState) => state.internet.tariffs,
    ],
    (bundles, internet) => {
        // возвращаемый функциями форматирования объект не согласован с ITariff - но повсеместно в коде используется как ITariff, требуется доработка ITariff.
        const formatBundles: ITariff[] = bundles.map(formattingBundleForCard);
        const formatInternets: ITariff[] = internet.map(formattingTariffForCard);

        return [
            ...formatBundles,
            ...formatInternets,
        ].sort((a, b) => a.weight - b.weight);
    },
);

export const selectInternetTariffByAlias = createSelector(
    [
        (state: TState) => state.internet.tariffs,
        (_: TState, alias: string | number) => alias,
    ],
    (tariffs, alias) => {
        const tariff = tariffs.find(
            ({ description }: IInternetPackage) => description?.alias === alias,
        );

        return tariff ? formattingTariffForCard(tariff) : null;
    },
);


export const selectCheckedFilters = createSelector(
    [
        (state: TState): ISelectedFilters[] => state?.filteredBundles?.selected,
        (state: TState): string => state?.filteredBundles?.groupKey,
    ],
    (selectedFilters, actualGroupKey) => selectedFilters.filter(
        filter => filter.group === actualGroupKey && !!filter.values.length,
    ),
);

export const selectIsEmpty = (state: TState): boolean => !selectCheckedFilters(state).length;

interface ITariffsByType {
    cityBundles: ITariff[],
    villageBundles: ITariff[],
    cityInternet: ITariff[],
    villageInternet: ITariff[],
}

export const selectTariffsByType = createSelector(
    selectAllTariffs,
    tariffs => {
        return tariffs.reduce((acc: ITariffsByType, tariff) => {
            tariff.isCottage === 0 && acc.cityBundles.push(tariff);
            tariff.type === 'bundles' && tariff.isCottage !== 0 && acc.villageBundles.push(tariff);
            tariff.type === 'internet' && tariff.isCottage === 0 && acc.cityInternet.push(tariff);
            tariff.type === 'internet' && tariff.isCottage !== 0 && acc.villageInternet.push(tariff);
            return acc;
        }, {
            cityBundles: [],
            villageBundles: [],
            cityInternet: [],
            villageInternet: [],
        });
    },
);


const getPrice = (tariff: ITariff) => tariff['priceAction'] || tariff['price'] || 0;

interface IMinCostedTariff {
    description: string,
    price: string,
    url: string,
}
export const selectMinCostedTariff = (filter: (tariff: ITariff) => void) => createSelector(
    selectAllTariffs,
    (tariffs): IMinCostedTariff | null => {
        const filteredTariffs = tariffs.filter(filter);

        if (filteredTariffs.length) {
            const sortedTariffs = [...filteredTariffs].sort((a, b) => getPrice(a) - getPrice(b));
            const minCostedTariff = sortedTariffs[0];

            return ({
                description: minCostedTariff?.seo_description || minCostedTariff?.description || '',
                price: getPrice(minCostedTariff).toString(),
                url: minCostedTariff?.alias && `full-buy?tariffName=${minCostedTariff?.alias}&monoTariff=true` || '',
            });
        }

        return null;
    },
);

export const selectAvailableOnInternetPageTariffs = ({ isCottage }: {
    isCottage?: boolean,
}) => createSelector(
    selectAllTariffs,
    (allTariffs: ITariff[]): ITariff[] =>
        allTariffs
            .filter((tariff: ITariff) =>
                tariff.availableOnInternetPage && isCottage === Boolean(tariff.isCottage)),
);

export const selectAndSortAvailableOnInternetPageTariffs = ({ isCottage }: {
    isCottage?: boolean,
}) => createSelector(
    selectAvailableOnInternetPageTariffs({ isCottage }),
    (tariffsAvailableOnInternetPage: ITariff[]): ITariff[] =>
        tariffsAvailableOnInternetPage
            .sort((tariffA, tariffB) => {
                const speedDiff = tariffB['speed'] - tariffA['speed'];
                if (speedDiff !== 0) {
                    return speedDiff;
                }

                const priceDiff = getPrice(tariffA) - getPrice(tariffB);
                if (priceDiff !== 0) {
                    return priceDiff;
                }

                return tariffA.type === 'internet' ? -1 : 1;
            }),
);
